import React, { FC, useCallback, ReactNode } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { useEditor } from '@craftjs/core'

import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'

import NumericClicker from '../widgets/NumericClicker'
import SmallText from '../widgets/SmallText'

import icons from '../../icons'

import ItemToolbar from './ItemToolbar'
import SaveButton from './SaveButton'
import PreviewButton from './PreviewButton'
import {
  getNumber,
} from '@kaidam/fc-shared/src/utils/number'

import {
  ILayout,
  ILayout_Options,
} from '@kaidam/fc-shared/src/types/layout'

import {
  extractSetValue,
} from './utils'

import {
  LAYOUT_HORIZONTAL_ALIGN_LEFT,
  LAYOUT_HORIZONTAL_ALIGN_CENTER,
  LAYOUT_HORIZONTAL_ALIGN_RIGHT,
  LAYOUT_HORIZONTAL_ALIGN_DEFAULT,
  LAYOUT_VERTICAL_ALIGN_TOP,
  LAYOUT_VERTICAL_ALIGN_MIDDLE,
  LAYOUT_VERTICAL_ALIGN_BOTTOM,
  LAYOUT_VERTICAL_ALIGN_DEFAULT,
  LAYOUT_NODE_TYPE_ROW,
  LAYOUT_NODE_TYPE_CELL,
  LAYOUT_CELL_TYPE_EMPTY,
  LAYOUT_CELL_TYPE_TEXT,
} from '@kaidam/fc-shared/src/constants'

const AlignLeftIcon = icons.alignLeft
const AlignCenterIcon = icons.alignCenter
const AlignRightIcon = icons.alignRight
const AlignTopIcon = icons.alignTop
const AlignMiddleIcon = icons.alignMiddle
const AlignBottomIcon = icons.alignBottom

interface LayoutToolbarProps {
  buttons?: ReactNode,
  buttonsBefore?: ReactNode,
  options: ILayout_Options,
  onChange: {
    (data: ILayout): void,
  },
}

const LayoutToolbar: FC<React.PropsWithChildren<LayoutToolbarProps>> = ({
  buttons,
  buttonsBefore,
  options,
  onChange,
}) => {

  const {
    actions,
    id,
    type,
    locked,
    cellType,
    ignoreSelection,
    width,
    horizontalAlign,
    verticalAlign,
    padding,
    margin,
    canChangeSize,
  } = useEditor((state, utils) => {
    let id = ''
    let type = ''
    let cellType = ''
    let locked = false
    let ignoreSelection = false
    let horizontalAlign = LAYOUT_HORIZONTAL_ALIGN_DEFAULT
    let verticalAlign = LAYOUT_VERTICAL_ALIGN_DEFAULT
    let width = 0
    let padding = 0
    let margin = 0
    let canChangeSize = false
    if(state.events.selected) {
      id = extractSetValue(state.events.selected)
      const node = state.nodes[id]
      if(node) {
        const props = node.data.props || {}
        const config = props.config || {}
        type = node.data.displayName
        if(type == LAYOUT_NODE_TYPE_CELL) {
          if(props.type == LAYOUT_CELL_TYPE_EMPTY) ignoreSelection = true
          cellType = props.type
          canChangeSize = true
          horizontalAlign = config.horizontal_align || LAYOUT_HORIZONTAL_ALIGN_DEFAULT
          verticalAlign = config.vertical_align || LAYOUT_VERTICAL_ALIGN_DEFAULT
          width = config.width || 1
          locked = node.data.props.config?.locked ? true : false
          padding = typeof(config.padding) == 'number' ?
            config.padding :
            1
        }
        else if(type == LAYOUT_NODE_TYPE_ROW) {
          const isTopLevel = utils.node(id).ancestors().length == 1
          canChangeSize = isTopLevel ? false : true
          width = config.width || 1
          margin = config.margin || 0
          locked = node.data.props.config?.locked ? true : false
        }
        else {
          id = ''
        }
      }
    }
    return {
      id,
      type,
      locked,
      cellType,
      ignoreSelection,
      width,
      padding,
      margin,
      horizontalAlign,
      verticalAlign,
      canChangeSize,
    }
  })

  const onChangeWidth = useCallback((amount: number) => {
    actions.setProp(id, props => {
      const config = props.config || {}
      config.width = (config.width || 1) + amount
      if(config.width < 1) config.width = 1
      props.config = config
    })
  }, [
    actions.setProp,
    id,
  ])

  const onChangePadding = useCallback((amount: number) => {
    actions.setProp(id, props => {
      const config = props.config || {}
      config.padding = getNumber(config.padding) + amount
      if(config.padding < 0) config.padding = 0
      props.config = config
    })
  }, [
    actions.setProp,
    id,
  ])

  const onChangeMargin = useCallback((amount: number) => {
    actions.setProp(id, props => {
      const config = props.config || {}
      config.margin = getNumber(config.margin, 0) + amount
      if(config.margin < 0) config.margin = 0
      props.config = config
    })
  }, [
    actions.setProp,
    id,
  ])

  const onChangeHorizontalAlign = useCallback((align: string) => {
    actions.setProp(id, props => {
      const config = props.config || {}
      config.horizontal_align = align
      props.config = config
    })
  }, [
    actions.setProp,
    id,
  ])

  const onChangeVerticalAlign = useCallback((align: string) => {
    actions.setProp(id, props => {
      const config = props.config || {}
      config.vertical_align = align
      props.config = config
    })
  }, [
    actions.setProp,
    id,
  ])

  let toolbar = null

  let baseToolbar = (
    <>
      {
        !options.hideSaveButton && (
          <SaveButton
            title={ options.saveButtonTitle }
            onChange={ onChange }
          />
        )
      }
      {
        buttonsBefore && (
          buttonsBefore 
        )
      }
      {
        !options.hidePreviewButton && (
          <PreviewButton
            options={ options }
          />
        )
      }
      {
        buttons && (
          <Box
            className='extraButtonWrapper'
            sx={{
              ml: 1,
            }}>
            { buttons }
          </Box>
        )
      }
    </>
  )

  if(id && !ignoreSelection) {
    toolbar = (
      <>
        <Box
          className='toolbar'
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {
            canChangeSize && (
              <>
                <Box
                  className='widthControls'
                >
                  <NumericClicker
                    title={ `width (${width}):` }
                    increaseTitle="grow"
                    decreaseTitle="shrink"
                    onChange={ onChangeWidth }
                  />
                </Box>
                <Box
                  className='paddingControls'
                >
                  <NumericClicker
                    title={ `padding (${padding}):` }
                    onChange={ onChangePadding }
                  />
                </Box>
              </>
            )
          }
          {
            type == 'row' && (
              <Box
                className='marginControls'
              >
                <NumericClicker
                  title={ `margin (${margin}):` }
                  increaseTitle="grow"
                  decreaseTitle="shrink"
                  onChange={ onChangeMargin }
                />
              </Box>
            )
          }
          
          {
            type == 'cell' && (
              <>
                <Box>
                  <Tooltip title="align left">
                    <span>
                      <IconButton
                        size="small"
                        className='button'
                        sx={{
                          mx: 0.5,
                        }}
                        disabled={ cellType == LAYOUT_CELL_TYPE_TEXT }
                        onClick={ () => onChangeHorizontalAlign(LAYOUT_HORIZONTAL_ALIGN_LEFT) }
                      >
                        <AlignLeftIcon color={ cellType == LAYOUT_CELL_TYPE_TEXT ? 'disabled' : horizontalAlign == LAYOUT_HORIZONTAL_ALIGN_LEFT ? 'secondary' : 'primary' } fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="align center">
                    <span>
                      <IconButton
                        size="small"
                        className='button'
                        sx={{
                          mx: 0.5,
                        }}
                        disabled={ cellType == LAYOUT_CELL_TYPE_TEXT }
                        onClick={ () => onChangeHorizontalAlign(LAYOUT_HORIZONTAL_ALIGN_CENTER) }
                      >
                        <AlignCenterIcon color={ cellType == LAYOUT_CELL_TYPE_TEXT ? 'disabled' : horizontalAlign == LAYOUT_HORIZONTAL_ALIGN_CENTER ? 'secondary' : 'primary' } fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="align right">
                    <span>
                      <IconButton
                        size="small"
                        className='button'
                        sx={{
                          mx: 0.5,
                        }}
                        disabled={ cellType == LAYOUT_CELL_TYPE_TEXT }
                        onClick={ () => onChangeHorizontalAlign(LAYOUT_HORIZONTAL_ALIGN_RIGHT) }
                      >
                        <AlignRightIcon color={ cellType == LAYOUT_CELL_TYPE_TEXT ? 'disabled' : horizontalAlign == LAYOUT_HORIZONTAL_ALIGN_RIGHT ? 'secondary' : 'primary' } fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    size="small"
                    className='button'
                    sx={{
                      mx: 0.5,
                    }}
                    onClick={ () => onChangeVerticalAlign(LAYOUT_VERTICAL_ALIGN_TOP) }
                  >
                    <AlignTopIcon color={ verticalAlign == LAYOUT_VERTICAL_ALIGN_TOP ? 'secondary' : 'primary' } fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    className='button'
                    sx={{
                      mx: 0.5,
                    }}
                    onClick={ () => onChangeVerticalAlign(LAYOUT_VERTICAL_ALIGN_MIDDLE) }
                  >
                    <AlignMiddleIcon color={ verticalAlign == LAYOUT_VERTICAL_ALIGN_MIDDLE ? 'secondary' : 'primary' } fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    className='button'
                    sx={{
                      mx: 0.5,
                    }}
                    onClick={ () => onChangeVerticalAlign(LAYOUT_VERTICAL_ALIGN_BOTTOM) }
                  >
                    <AlignBottomIcon color={ verticalAlign == LAYOUT_VERTICAL_ALIGN_BOTTOM ? 'secondary' : 'primary' } fontSize="small" />
                  </IconButton>
                </Box>
              </>
            )
          }
        </Box>
      </>
    )
  }
  else {
    toolbar = baseToolbar
  }
  
  return (
    <Box
      sx={{
        // height: '100%',
        // display: 'flex',
        // flexDirection: 'column',
        // justifyContent: 'flex-start',
      }}
    >
      {
        options.lockedCells && locked ? (
          <SmallText>
            This item is locked
          </SmallText>
        ) : toolbar
      }
    </Box>
  )

}

export default LayoutToolbar